import type { ColorType } from '../core/enums'
import type { Bus } from './bus'
import type { GameSession } from './session'

/**
 * Decides which parked bus to send out next, well enough that a headless run
 * measures the rules rather than the bot.
 *
 * Three facts from the level data (docs/GAMEPLAY-CHECKLIST.md §9b) shape it:
 *
 *  - Per colour, passengers exactly equal that colour's bus capacity, so every
 *    bus must eventually be dispatched and filled. A dispatch is never wasted,
 *    only mistimed.
 *  - A bus never departs unless it fills, so one on the road holds its slot
 *    until its colour has been supplied.
 *  - Both queues feed one bounded waiting area, so a colour with no bus on the
 *    road crowds out one that has.
 *
 * Hence: keep the road stocked with the colours about to leave the queues, and
 * when the bus you need is boxed in, move whatever is boxing it in.
 */

/** How far down each queue to look. */
export const PLANNER_LOOKAHEAD = 60

export interface PlannerOptions {
  /** Tie-break noise, keyed by bus id. Lets restarts explore different plans. */
  jitter?: (busId: number) => number
}

/** 0 = already waiting in the area; larger = further down a queue. */
export function urgencyByColour(session: GameSession): Map<ColorType, number> {
  const urgency = new Map<ColorType, number>()
  const note = (colour: ColorType, rank: number) => {
    const current = urgency.get(colour)
    if (current === undefined || rank < current) urgency.set(colour, rank)
  }
  for (const area of session.areas) {
    for (const member of area.crowd) note(member.color, 0)
  }
  for (const queue of session.queues) {
    queue.upcomingColors(PLANNER_LOOKAHEAD).forEach((colour, i) => note(colour, i + 1))
  }
  return urgency
}

/** Unfilled seats on the road, per colour. */
export function coverageByColour(session: GameSession): Map<ColorType, number> {
  const coverage = new Map<ColorType, number>()
  for (const bus of session.busesOnRoad) {
    if (bus.isFull) continue
    coverage.set(bus.color, (coverage.get(bus.color) ?? 0) + (bus.capacity - bus.passengers))
  }
  return coverage
}

/** Walk the blocking chain to the bus that has to move first. */
export function headOfBlockingChain(session: GameSession, bus: Bus): Bus | null {
  const seen = new Set<number>()
  let current: Bus | null = bus
  while (current && !seen.has(current.id)) {
    seen.add(current.id)
    const blocker: Bus | null = session.blockerOf(current)
    if (!blocker) return current
    current = blocker
  }
  return null
}

/** How many of each colour are standing in a waiting area right now. */
export function waitingByColour(session: GameSession): Map<ColorType, number> {
  const waiting = new Map<ColorType, number>()
  for (const area of session.areas) {
    for (const m of area.crowd) waiting.set(m.color, (waiting.get(m.color) ?? 0) + 1)
  }
  return waiting
}

/**
 * Every bus worth dispatching this tick, best first.
 *
 * Split out of `chooseDispatch` so a search can consider the runner-up instead
 * of always taking the head — see `scripts/search.ts`. The **last-slot guard**
 * is deliberately not applied here: it is the greedy bot's caution, and a
 * search should be free to look past it.
 */
export function rankDispatchCandidates(
  session: GameSession,
  options: PlannerOptions = {},
): Bus[] {
  if (session.roadSpace <= 0) return []

  const jitter = options.jitter ?? (() => 0)
  const urgency = urgencyByColour(session)
  const coverage = coverageByColour(session)

  const parked = session.buses.filter((b) => b.state === 'Parked')
  if (parked.length === 0) return []

  const waitingNow = waitingByColour(session)

  // Only colours that need serving right now are worth a road slot.
  //
  // A bus never departs unless it fills, so a slot given to a colour that is
  // not coming up is held for a long time — long enough for the waiting area to
  // fill with a colour that has no bus, which is a dead level. Every stall the
  // diagnostics turned up looked the same: the road full of empty buses whose
  // colour was nowhere near the front of a queue, while the area was packed
  // with one that was.
  const active = new Set<ColorType>()
  for (const colour of waitingNow.keys()) active.add(colour)
  for (const queue of session.queues) {
    const front = queue.peekFrontColor()
    if (front !== null) active.add(front)
  }

  const candidates: { bus: Bus; score: number }[] = []

  for (const bus of parked) {
    if (!active.has(bus.color)) continue

    // Someone is already on the way for this colour with room to spare.
    const inArea = waitingNow.get(bus.color) ?? 0
    const covered = coverage.get(bus.color) ?? 0
    if (covered > 0 && covered >= Math.max(inArea, 1)) continue

    const target = session.canDispatch(bus) ? bus : headOfBlockingChain(session, bus)
    if (!target || !session.canDispatch(target)) continue

    // Clearing a path costs a slot too, so an unblocked bus wins a tie.
    const detour = target === bus ? 0 : 6
    const want = urgency.get(bus.color) ?? PLANNER_LOOKAHEAD
    candidates.push({ bus: target, score: want + detour + jitter(bus.id) })
  }

  candidates.sort((a, b) => a.score - b.score)
  return candidates.map((c) => c.bus)
}

export function chooseDispatch(session: GameSession, options: PlannerOptions = {}): Bus | null {
  const ranked = rankDispatchCandidates(session, options)
  if (ranked.length === 0) return null

  // Guard the last slot for a colour already standing in the area, so a
  // speculative dispatch cannot lock out the only bus that could unload it.
  const waitingNow = waitingByColour(session)
  if (session.roadSpace === 1 && waitingNow.size > 0) {
    const urgent = ranked.find((bus) => waitingNow.has(bus.color))
    if (urgent) return urgent
    const someoneCanServe = session.busesOnRoad.some(
      (b) => !b.isFull && waitingNow.has(b.color),
    )
    if (someoneCanServe) return null
  }

  return ranked[0]
}
